import React, { useState } from "react";
import { Crown, User } from "lucide-react";

interface UserAvatarProps {
  photoURL?: string | null;
  name: string;
  uid?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
  showCrown?: boolean;
  isHost?: boolean;
}

const SIZE_MAP = {
  xs: "w-5 h-5 text-[10px]",
  sm: "w-6 h-6 text-xs",
  md: "w-8 h-8 text-sm",
  lg: "w-10 h-10 text-base",
  xl: "w-16 h-16 text-2xl",
};

const CROWN_SIZE_MAP = {
  xs: "w-2.5 h-2.5 -top-1 -right-0.5",
  sm: "w-3 h-3 -top-1.5 -right-1",
  md: "w-3.5 h-3.5 -top-1.5 -right-1",
  lg: "w-4 h-4 -top-2 -right-1.5",
  xl: "w-6 h-6 -top-2.5 -right-2",
};

// Generates consistent distinct gradient based on name/uid
function getAvatarGradient(seed: string): string {
  const gradients = [
    "from-orange-500 to-amber-500",
    "from-emerald-500 to-teal-500",
    "from-indigo-500 to-purple-500",
    "from-pink-500 to-rose-500",
    "from-cyan-500 to-blue-500",
    "from-violet-500 to-fuchsia-500",
    "from-amber-500 to-red-500",
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % gradients.length;
  return gradients[index];
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
  photoURL,
  name,
  uid = "",
  size = "md",
  className = "",
  showCrown = false,
  isHost = false,
}) => {
  const [imageError, setImageError] = useState(false);

  const initial = (name || "Singer").trim().charAt(0).toUpperCase() || "S";
  const sizeClasses = SIZE_MAP[size];
  const crownClasses = CROWN_SIZE_MAP[size];
  const gradient = getAvatarGradient(name + uid);

  // Default fallback URL using Dicebear SVG with encoded seed
  const fallbackUrl = uid || name
    ? `https://api.dicebear.com/7.x/bottts/svg?seed=${encodeURIComponent(uid || name)}`
    : null;

  const src = !imageError ? (photoURL || fallbackUrl) : null;

  return (
    <div className={`relative inline-block shrink-0 ${className}`}>
      {src ? (
        <img
          src={src}
          alt={name}
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
          onError={() => setImageError(true)}
          className={`${sizeClasses} rounded-full object-cover border ${
            isHost ? "border-primary" : "border-border"
          } bg-card shadow-sm`}
        />
      ) : (
        <div
          className={`${sizeClasses} rounded-full bg-gradient-to-tr ${gradient} flex items-center justify-center font-bold text-white shadow-sm border ${
            isHost ? "border-primary" : "border-border"
          } select-none`}
        >
          {initial ? <span>{initial}</span> : <User className="w-1/2 h-1/2" />}
        </div>
      )}

      {(showCrown || isHost) && (
        <Crown
          className={`${crownClasses} text-primary absolute fill-primary drop-shadow`}
        />
      )}
    </div>
  );
};
